import React, { useEffect, useMemo, useState } from 'react';
import { Box, HStack, Tag, TagLabel, TagCloseButton, Wrap, WrapItem, Input, Button, Popover, PopoverTrigger, PopoverContent, PopoverBody, VStack, Text, useToast } from '@chakra-ui/react';
import { supabase } from '../../supabaseClient';
import { SUGGESTED_TAGS, tagColor } from '../../utils/submissionTags';

// Inline editor for submissions.tags. Saves on every change so admins/phlebs
// don't need a separate Save button.
//
// Props:
//   submissionId  — required for persistence
//   value         — array of current tags
//   onChange      — (nextTags) => void  optional, for parent state sync
//   readOnly      — show chips only, no add/remove (for lab portal display)
export default function TagEditor({ submissionId, value = [], onChange, readOnly = false }) {
  const toast = useToast();
  const [tags, setTags] = useState(Array.isArray(value) ? value : []);
  const [draft, setDraft] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);

  useEffect(() => {
    setTags(Array.isArray(value) ? value : []);
  }, [value]);

  const persist = async (next) => {
    setTags(next);
    onChange?.(next);
    if (!submissionId) return;
    const { error } = await supabase
      .from('submissions')
      .update({ tags: next })
      .eq('id', submissionId);
    if (error) {
      toast({ title: 'Could not save tags', description: error.message, status: 'error', duration: 4000 });
    }
  };

  const addTag = (raw) => {
    const t = (raw || '').trim();
    if (!t) return;
    if (tags.some((x) => x.toLowerCase() === t.toLowerCase())) return;
    persist([...tags, t]);
    setDraft('');
  };

  const removeTag = (t) => persist(tags.filter((x) => x !== t));

  const remainingSuggestions = useMemo(
    () => SUGGESTED_TAGS.filter((s) => !tags.some((t) => t.toLowerCase() === s.toLowerCase())),
    [tags],
  );

  if (readOnly) {
    if (tags.length === 0) return null;
    return (
      <Wrap spacing={1}>
        {tags.map((t) => (
          <WrapItem key={t}>
            <Tag size="sm" colorScheme={tagColor(t)} borderRadius="full">{t}</Tag>
          </WrapItem>
        ))}
      </Wrap>
    );
  }

  return (
    <Box>
      <Wrap spacing={1} align="center">
        {tags.map((t) => (
          <WrapItem key={t}>
            <Tag size="sm" colorScheme={tagColor(t)} borderRadius="full">
              <TagLabel>{t}</TagLabel>
              <TagCloseButton onClick={() => removeTag(t)} />
            </Tag>
          </WrapItem>
        ))}
        <WrapItem>
          <Popover isOpen={popoverOpen} onClose={() => setPopoverOpen(false)} placement="bottom-start">
            <PopoverTrigger>
              <Button size="xs" variant="outline" onClick={() => setPopoverOpen(true)}>
                + Add tag
              </Button>
            </PopoverTrigger>
            <PopoverContent w="260px">
              <PopoverBody>
                <VStack align="stretch" spacing={2}>
                  <HStack>
                    <Input
                      size="sm"
                      placeholder="Custom tag…"
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); addTag(draft); }
                      }}
                      autoFocus
                    />
                    <Button size="sm" colorScheme="blue" onClick={() => addTag(draft)} isDisabled={!draft.trim()}>
                      Add
                    </Button>
                  </HStack>
                  {remainingSuggestions.length > 0 && (
                    <>
                      <Text fontSize="2xs" color="gray.500" fontWeight="700" textTransform="uppercase" letterSpacing="0.05em">
                        Suggestions
                      </Text>
                      <Wrap spacing={1}>
                        {remainingSuggestions.map((s) => (
                          <WrapItem key={s}>
                            <Tag
                              size="sm"
                              colorScheme={tagColor(s)}
                              borderRadius="full"
                              cursor="pointer"
                              onClick={() => { addTag(s); setPopoverOpen(false); }}
                            >
                              {s}
                            </Tag>
                          </WrapItem>
                        ))}
                      </Wrap>
                    </>
                  )}
                </VStack>
              </PopoverBody>
            </PopoverContent>
          </Popover>
        </WrapItem>
      </Wrap>
    </Box>
  );
}
